import mixpanel from 'mixpanel-browser';
import { Logger } from '../support/Logger';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { EnvConfiguration } from '../config/EnvConfiguration';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { APP_USER_INTERACTED } from '../AppContainer';

export const SESSION_REPLAY_INIT_COMPLETE = 'SessionReplayService.initComplete';

// Admin users are currently included in session replays. The admin-exclusion path
// (stopActiveReplay + deferred identify in MixpanelService) is left wired up so we
// can re-enable by flipping this flag, without re-deriving the event ordering.
export const EXCLUDE_ADMINS_FROM_REPLAY = false;

// sessionStorage key tracking which principal a Mixpanel replay was started for.
// 'anonymous' or a userId. Used by enforceReplayOwnership to detect when a leftover
// replay (resumed from IDB after a page reload) belongs to a different principal
// than the current one — e.g. after sign-out + sign-in-as-different-user — and stop
// it before mixpanel.identify can re-tag the leftover with the new user's id.
//
// We store this in sessionStorage (per tab/window) rather than localStorage (shared
// across tabs) so the marker's lifetime mirrors Mixpanel's per-tab recording — the
// recorder is keyed by mixpanel.get_tab_id() which is also stored in sessionStorage.
// This avoids a multi-tab bug where one tab's logout would clear a shared marker
// while another tab's per-tab recording was still active and unmarked, leaving it
// undetected when that tab later signed in as a different user.
const REPLAY_USER_MARKER_KEY = 'mp-replay-tagged-user';
const ANONYMOUS_MARKER = 'anonymous';

/**
 * Owns the lifecycle of Mixpanel session replay.
 *
 * Replays start on user interaction for eligible users. Auto-test users are
 * excluded upstream by the empty MIXPANEL_TOKEN pattern in AppContainer.
 *
 * Mixpanel auto-sampling is disabled (record_sessions_percent omitted in
 * MixpanelService.init), so start_session_recording() is the only path to a
 * replay. We listen on every user interaction and check whether a replay is
 * already active; if not, we start one. This handles cases where Mixpanel
 * ended the prior replay (idle timeout, max duration) — the next interaction
 * starts a fresh one.
 *
 * Cross-user replay protection: Mixpanel persists active replays in IndexedDB
 * across page reloads. After a sign-out the leftover replay can resume on the
 * next page load and, if the next user signs in, get re-tagged with their
 * distinct_id — producing a single replay tagged as user B that contains user
 * A's session history. To prevent this, we write a localStorage marker
 * identifying the principal whose replay is active. On every app init,
 * MixpanelService calls enforceReplayOwnership BEFORE mixpanel.identify; if
 * the marker doesn't match the current principal, we stop the leftover so the
 * next interaction starts a fresh replay tagged for the right user.
 *
 * Fires SessionReplayService.initComplete exactly once per init() call after
 * settled state is observable. Consumers can subscribe to coordinate work
 * that depends on the replay state being known.
 */
export class SessionReplayService {
    static inject = () => [
        BufferedEventBus,
        Logger,
        EnvConfiguration
    ];
    constructor(
        private events: BufferedEventBus,
        private log: Logger,
        private config: EnvConfiguration
    ) {}

    private principal?: AppPrincipal;

    init = ({ principal }: { principal?: AppPrincipal }) => {
        this.principal = principal;

        if (!this.config.MIXPANEL_TOKEN) {
            this.events.emit(SESSION_REPLAY_INIT_COMPLETE);
            return;
        }
        if (!this.config.SESSION_REPLAY_ENABLED) {
            this.events.emit(SESSION_REPLAY_INIT_COMPLETE);
            return;
        }
        if (EXCLUDE_ADMINS_FROM_REPLAY && principal?.isAdmin) {
            this.log.info('SessionReplayService: admin user, skipping');
            // Sign-in continues any replay started during the prior anonymous (logged-out) state,
            // re-attributing it to the now-identified user. For admins we don't want any replay —
            // including one that was legitimate in the anonymous state. Explicitly stop it.
            // initComplete fires once stopActiveReplay settles.
            this.stopActiveReplay();
            return;
        }

        this.events.on(APP_USER_INTERACTED, () => {
            this.startReplay();
        });

        this.events.emit(SESSION_REPLAY_INIT_COMPLETE);
    };

    /**
     * Verifies any leftover replay (resumed from Mixpanel's IDB persistence) belongs
     * to the current principal. Called by MixpanelService after mixpanel.init but
     * BEFORE mixpanel.identify — running it after identify would be too late, since
     * identify retroactively re-tags the leftover replay with the new distinct_id.
     *
     * If the leftover is owned by a different principal, stops it so the next user
     * interaction starts a fresh replay tagged for the current principal. The
     * anonymous-to-identified transition (e.g. fresh sign-up) is allowed since it
     * is the same browsing session continuing under a newly-acquired identity.
     */
    enforceReplayOwnership = async (principal?: AppPrincipal): Promise<void> => {
        if (!this.config.MIXPANEL_TOKEN || !this.config.SESSION_REPLAY_ENABLED) {
            return;
        }

        const marker = this.readMarker();
        if (!marker) {
            return;
        }

        const expected = this.getMarkerForPrincipal(principal);

        // Wait briefly for any IDB-resumed leftover replay to become observable;
        // rrweb is lazy-loaded after mixpanel.init returns, so the replayId isn't
        // readable immediately.
        const replayId = await this.waitForReplayId(2000);
        if (!replayId) {
            // No active leftover; clear the stale marker and proceed.
            this.clearMarker();
            return;
        }

        if (marker === expected) {
            this.log.info(`SessionReplayService: continuing replay (replayId=${replayId}, taggedAs=${marker})`);
            return;
        }

        if (marker === ANONYMOUS_MARKER && expected !== ANONYMOUS_MARKER) {
            // Legit anonymous-to-identified transition (sign-up flow). Mixpanel
            // re-tags the leftover anonymous replay with the new identity via the
            // upcoming mixpanel.identify call — that is the intended merge behavior.
            // Update the marker so a subsequent reload doesn't see this as a mismatch.
            this.writeMarker(expected);
            this.log.info(`SessionReplayService: continuing replay through anon→identified transition (replayId=${replayId}, newOwner=${expected})`);
            return;
        }

        this.log.info(`SessionReplayService: stopping mismatched leftover replay (replayId=${replayId}, was=${marker}, expected=${expected})`);
        await this.stopReplay();
    };

    private startReplay = () => {
        if (this.getReplayId()) {
            // a replay is already active; nothing to do.
            return;
        }
        try {
            mixpanel.start_session_recording();
        } catch (err: any) {
            this.log.error(new Error(`Failed to start session replay: ${err.message}`));
            return;
        }
        this.writeMarker(this.getMarkerForPrincipal(this.principal));
        this.observeStartOutcome();
    };

    private observeStartOutcome = () => {
        // start_session_recording is effectively async on the first call per tab
        // (rrweb bundle is lazy-loaded). Poll until we see a replayId or time out,
        // so the log accurately reflects Mixpanel's settled state instead of
        // returning a false negative during bundle load.
        const startedAt = Date.now();
        const timeoutMs = 2000;
        const intervalMs = 50;
        const poll = () => {
            const replayId = this.getReplayId();
            if (replayId) {
                this.log.info(`SessionReplayService: started new replay (replayId=${replayId})`);
                return;
            }
            if (Date.now() - startedAt >= timeoutMs) {
                this.log.info(`SessionReplayService: no replay id detected after ${timeoutMs}ms`);
                return;
            }
            setTimeout(poll, intervalMs);
        };
        poll();
    };

    private stopReplay = async () => {
        const beforeId = this.getReplayId();
        if (!beforeId) {
            this.log.info('SessionReplayService: verified replay already stopped');
            this.clearMarker();
            return;
        }
        try {
            // stop_session_recording returns a Promise; await it so the after-state
            // we read reflects Mixpanel's actual settled state rather than the
            // pre-resolution state.
            await mixpanel.stop_session_recording();
        } catch (err: any) {
            this.log.error(new Error(`Failed to stop session replay: ${err.message}`));
            return;
        }
        this.clearMarker();
        const afterId = this.getReplayId();
        if (!afterId) {
            this.log.info(`SessionReplayService: stopped replay (replayId=${beforeId})`);
        } else {
            this.log.info(`SessionReplayService: stop did not clear replay id (was=${beforeId}, still=${afterId})`);
        }
    };

    private stopActiveReplay = async () => {
        const replayId = await this.waitForReplayId(2000);
        if (replayId) {
            this.log.info(`SessionReplayService: stopping active replay for admin user (replayId=${replayId})`);
            await this.stopReplay();
        }
        this.events.emit(SESSION_REPLAY_INIT_COMPLETE);
    };

    private waitForReplayId = async (timeoutMs: number): Promise<string | undefined> => {
        const startedAt = Date.now();
        const intervalMs = 50;
        while (Date.now() - startedAt < timeoutMs) {
            const replayId = this.getReplayId();
            if (replayId) {
                return replayId;
            }
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
        return undefined;
    };

    private getReplayId = (): string | undefined => {
        const props = mixpanel.get_session_recording_properties();
        return '$mp_replay_id' in props ? props.$mp_replay_id : undefined;
    };

    private getMarkerForPrincipal = (principal?: AppPrincipal): string => {
        if (principal && !isAnonymousViewer(principal)) {
            return principal.userId;
        }
        return ANONYMOUS_MARKER;
    };

    private readMarker = (): string | null => {
        try {
            return sessionStorage.getItem(REPLAY_USER_MARKER_KEY);
        } catch {
            // sessionStorage can throw in private-browsing modes; safety net is degraded
            // but the rest of the service still functions.
            return null;
        }
    };

    private writeMarker = (value: string): void => {
        try {
            sessionStorage.setItem(REPLAY_USER_MARKER_KEY, value);
        } catch {
            // see readMarker
        }
    };

    private clearMarker = (): void => {
        try {
            sessionStorage.removeItem(REPLAY_USER_MARKER_KEY);
        } catch {
            // see readMarker
        }
    };
}
